import type { z } from 'zod';

const API_BASE = import.meta.env.VITE_API_URL || '';

interface ApiOptions extends Omit<RequestInit, 'body'> {
  body?: unknown;
  params?: Record<string, string | undefined>;
}

class ApiError extends Error {
  status: number;
  data: unknown;

  constructor(message: string, status: number, data: unknown) {
    super(message);
    this.name = 'ApiError';
    this.status = status;
    this.data = data;
  }
}

function getToken(): string | null {
  return localStorage.getItem('auth-token');
}

async function request<T>(
  path: string,
  options: ApiOptions = {},
  schema?: z.ZodType<T>,
): Promise<T> {
  const { body, params, ...fetchOptions } = options;

  // Build URL with query params
  let url = `${API_BASE}${path}`;
  if (params) {
    const searchParams = new URLSearchParams();
    for (const [key, value] of Object.entries(params)) {
      if (value !== undefined) {
        searchParams.set(key, value);
      }
    }
    const qs = searchParams.toString();
    if (qs) url += `?${qs}`;
  }

  // Build headers
  const headers = new Headers(fetchOptions.headers as Record<string, string>);
  const token = getToken();
  if (token) {
    headers.set('Authorization', `Bearer ${token}`);
  }

  // Handle JSON body
  if (body !== undefined && !(body instanceof FormData)) {
    headers.set('Content-Type', 'application/json');
  }

  const res = await fetch(url, {
    ...fetchOptions,
    headers,
    body: body instanceof FormData ? body : body !== undefined ? JSON.stringify(body) : undefined,
  });

  // Handle non-JSON responses
  const contentType = res.headers.get('content-type');
  let data: unknown;
  if (contentType?.includes('application/json')) {
    data = await res.json();
  } else {
    data = await res.text();
  }

  if (!res.ok) {
    const message =
      typeof data === 'object' && data && 'error' in data
        ? (data as { error: string }).error
        : `HTTP ${res.status}`;
    throw new ApiError(message, res.status, data);
  }

  // Validate with Zod schema if provided
  if (schema) {
    return schema.parse(data);
  }

  return data as T;
}

// Convenience methods
export const api = {
  get: <T>(path: string, options?: ApiOptions, schema?: z.ZodType<T>) =>
    request<T>(path, { ...options, method: 'GET' }, schema),

  post: <T>(path: string, body?: unknown, options?: ApiOptions, schema?: z.ZodType<T>) =>
    request<T>(path, { ...options, method: 'POST', body }, schema),

  put: <T>(path: string, body?: unknown, options?: ApiOptions, schema?: z.ZodType<T>) =>
    request<T>(path, { ...options, method: 'PUT', body }, schema),

  delete: <T>(path: string, options?: ApiOptions, schema?: z.ZodType<T>) =>
    request<T>(path, { ...options, method: 'DELETE' }, schema),

  upload: <T>(path: string, formData: FormData, options?: ApiOptions, schema?: z.ZodType<T>) =>
    request<T>(path, { ...options, method: 'POST', body: formData }, schema),
};

export type { ApiOptions };
export { ApiError };
